import {
  Icon,
  IconAlertCircle,
  IconAlertTriangle,
  IconBuildingBank,
  IconCarCrash,
  IconCircleCheck,
  IconCircleX,
  IconFall,
  IconInfoCircle,
  IconReceiptRefund,
  IconUser,
  IconUserExclamation,
} from '@tabler/icons-react';
import {
  ClaimDamageWithRelations,
  ClaimWithRelations,
  CreateClaimRequestBody,
  FetchClaimDetailResponseBody,
  MaterialDamageWithRelations,
  SearchEntityRequestQuery,
  SearchEntityResponseBody,
  UpdateClaimRequestBody,
  VehicleWithRelations,
  consistencyIdentityOf,
  getClaimDamage,
  validateClaimantVictimConsistency,
} from 'bff';
import {
  DamageTypes,
  NaturalPerson as INaturalPerson,
  LegalPerson as ILegalPerson,
  VictimTypes,
  Prisma,
  PersonDamageDetail,
  SexTypes,
  IdentificationTypes,
  PersonParentTypes,
  VehicleTypes,
  VehicleUseTypes,
  VehicleCategoryTypes,
  CoverageTypes,
  VictimRoleTypes,
  VictimRelationshipTypes,
  PolicyHolderRelationshipTypes,
  DriverOwnershipTypes,
  LegalPersonTypes,
} from 'database';
import es from 'date-fns/locale/es';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import {
  DEFAULT_COUNTRY_CODE,
  argentinaCities,
  countryAndStates,
  isCatalogBackedVehicleType,
  paraguayCities,
} from 'piramid-constants';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Control,
  NonUndefined,
  Path,
  UseControllerReturn,
  UseFormProps,
  UseFormReturn,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  Badge,
  Button,
  Calendar,
  Combobox,
  ComboboxOption,
  ComboboxProps,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  NumberField,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui';
import {
  buildUserFullName,
  currencyFormat,
  getCountryLabel,
  getStateLabel,
  isMinorAt,
  nativeEnumToArray,
  toTitleCase,
} from 'utils';

import {
  ClaimUpdateContext as ClaimUpdateContextType,
  ClaimUpdateSegment,
  useSafeClaimUpdate,
} from '../contexts/ClaimUpdateContext';
import { EditClaimButton } from './EditClaimButton';
import { CalendarIcon } from 'lucide-react';
import { ContextAwareCombobox, SelectAwaited } from '..';

type NaturalPersonPopulated = Prisma.NaturalPersonGetPayload<{
  include: {
    parents: true;
  };
}>;

export const damageIcons: {
  [K in DamageTypes]: Icon;
} = {
  recovery: IconReceiptRefund,
  material: IconCarCrash,
  person: IconFall,
};

const personTypesIcons: {
  [K in VictimTypes]: Icon;
} = {
  natural_person: IconUser,
  legal_person: IconBuildingBank,
};

const CardLabel = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'font-bold flex flex-row items-center space-x-2',
        className,
      )}
    >
      {children}
    </div>
  );
};

const CardValue = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn('', className)}>{children || '—'}</div>;
};

const CardAttribute = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('flex text-sm flex-col space-y-1', className)}>
      {children}
    </div>
  );
};

const CardGroup = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('rounded-2xl border bg-card p-4', className)}>
      {children}
    </div>
  );
};

const CardGroupLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <CardLabel className={cn('text-left', className)}>{children}</CardLabel>
  );
};

const Claimant = ({
  data,
  segment,
  prefix = 'victim',
}: {
  data: Pick<
    FetchClaimDetailResponseBody['claimants'][number],
    'type' | 'legal_person' | 'natural_person'
  >;
  segment: ClaimUpdateSegment;
  prefix?: `victim` | `claimants.${number}`;
}) => {
  switch (data.type) {
    case 'legal_person':
      return (
        <LegalPerson
          segment={segment}
          prefix={prefix}
          data={data.legal_person!}
        />
      );

    case 'natural_person':
      return (
        <NaturalPerson
          segment={segment}
          prefix={prefix}
          data={data.natural_person!}
        />
      );
  }
};

const LegalPerson = ({
  data,
  className,
  segment,
  prefix: _prefix = 'victim',
}: {
  data: ILegalPerson;
  className?: string;
  segment: ClaimUpdateSegment;
  prefix?: `victim` | `claimants.${number}`;
}) => {
  const t = useTranslations();

  const prefix = `${_prefix}.legal_person` as const;

  const fields: FieldSpec[] = [
    {
      label: 'Tipo',
      view: {
        type: 'literal',
        literal: 'Persona jurídica',
      },
    },
    {
      label: 'Entidad',
      view: {
        type: 'accessor',
        accessor: () => (data.type ? t(`legal_person.type.${data.type}`) : '—'),
      },
      edit: {
        type: 'select',
        formFieldName: `${prefix}.type`,
        options: nativeEnumToArray(LegalPersonTypes).map((type) => ({
          label: t(`legal_person.type.${type}`),
          value: type,
        })),
      },
    },
    {
      label: 'Razón social',
      view: {
        type: 'accessor',
        accessor: () => data.social_reason || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.social_reason`,
      },
    },
    {
      label: 'CUIT',
      view: {
        type: 'accessor',
        accessor: () => data.cuit || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.cuit`,
      },
    },
    {
      label: 'Provincia',
      view: {
        type: 'accessor',
        accessor: () => getStateLabel(DEFAULT_COUNTRY_CODE, data.state) || '—',
      },
      edit: {
        type: 'select',
        formFieldName: `${prefix}.state`,
        options:
          countryAndStates
            .find((country) => country.code === DEFAULT_COUNTRY_CODE)
            ?.states.map((state) => ({
              label: state.name,
              value: state.code,
            })) || [],
      },
    },
    {
      label: 'Ciudad',
      view: {
        type: 'accessor',
        accessor: () => data.city || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.city`,
      },
    },
    {
      label: 'Dirección',
      view: {
        type: 'accessor',
        accessor: () => data.address || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.address`,
      },
    },
    {
      label: 'Código Postal',
      view: {
        type: 'accessor',
        accessor: () => data.zip_code || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.zip_code`,
      },
    },
    {
      label: 'Correo',
      view: {
        type: 'accessor',
        accessor: () => data.email || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.email`,
      },
    },
  ];

  return (
    <GroupedFieldsSegmentRender
      className={className}
      segment={segment}
      fields={fields}
    />
  );
};

type FieldViewSpec =
  | {
      type: 'literal';
      literal: string;
    }
  | {
      type: 'accessor';
      accessor: (row: UpdateClaimRequestBody) => string;
    };

type FieldEditSpec =
  | {
      type: 'number';
      formFieldName: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'select';
      options: { label: string; value: string }[];
      formFieldName: Path<UpdateClaimRequestBody>;
      resetOnFieldChange?: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'text';
      formFieldName: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'textarea';
      formFieldName: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'date';
      formFieldName: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'check';
      formFieldName: Path<UpdateClaimRequestBody>;
    }
  | {
      type: 'toggle';
      checked: boolean;
      onCheckedChange: (checked: boolean) => void;
    }
  | {
      type: 'combobox';
      formFieldName: Path<UpdateClaimRequestBody>;
      resetOnFieldChange?: Path<UpdateClaimRequestBody>;
      prefetch?: boolean;
      dependencyState?: Path<UpdateClaimRequestBody>[];
      disabled?: boolean;
      placeholder: string;
      loadOptions: (
        search: string,
        ctx: UpdateClaimRequestBody,
      ) => ReturnType<ComboboxProps<ComboboxOption>['loadOptions']>;
      // Resolves the label shown in the trigger for the current stored value, for fields
      // whose value is a code rather than the display text (e.g. provincia stores a state code).
      getValueLabel?: (value: string, ctx: UpdateClaimRequestBody) => string;
    }
  | {
      type: 'select-awaited';
      formFieldName: Path<UpdateClaimRequestBody>;
      virtualized?: boolean;
      options: ComboboxOption[];
      resetOnFieldChange?: Path<UpdateClaimRequestBody>;
    };

type FieldSpec = {
  label: string;
  adornment?: React.ReactNode;
  view: FieldViewSpec;
  edit?: FieldEditSpec;
  hide?: boolean;
  defaultValue?: unknown;
};

const FieldViewRender = ({ field }: { field: FieldSpec }) => {
  const update = useSafeClaimUpdate();

  switch (field.view.type) {
    case 'literal':
      return field.view.literal || ' —';

    case 'accessor':
      return field.view.accessor(update.form?.getValues() || {}) || ' —';
  }
};

const DateField = ({
  spec,
  control,
}: {
  spec: Exclude<FieldSpec, { edit: undefined | null }>;
  control: UseControllerReturn;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const field = control.field;

  return (
    <Popover open={isOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            size='sm'
            data-test-id='date-picker'
            variant={'outline'}
            className={cn(
              'w-full pl-3 text-left font-normal',
              !control.field.value && 'text-muted-foreground',
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            {control.field.value ? (
              format(new Date(control.field.value), 'PPP', {
                locale: es,
              })
            ) : (
              <span>Selecciona una fecha</span>
            )}
            <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={field.value}
          onSelect={(day) => {
            field.onChange(day);
            setIsOpen(!isOpen);
          }}
          disabled={(date) =>
            date > new Date() || date < new Date('1900-01-01')
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const ComboboxField = ({
  spec,
  control,
}: {
  spec: Omit<FieldSpec, 'edit'> & {
    edit: Extract<FieldSpec['edit'], { type: 'combobox' }>;
  };
  control: UseControllerReturn;
}) => {
  const form = useFormContext<UpdateClaimRequestBody>();

  const resetReactiveValue = spec.edit.resetOnFieldChange
    ? form.watch(spec.edit.resetOnFieldChange)
    : '';

  const dependencyState = useMemo(() => {
    if (!spec.edit.dependencyState) return [];

    return form.watch(spec.edit.dependencyState);
  }, [spec.edit.dependencyState ? form.watch(spec.edit.dependencyState) : '']);

  return (
    <ContextAwareCombobox
      withClearOnReset={!!spec.edit.resetOnFieldChange}
      //@ts-ignore
      resetReactiveValue={resetReactiveValue}
      prefetch={spec.edit.prefetch}
      value={control.field.value}
      defaultValue={
        control.field.value
          ? {
              label: spec.edit.getValueLabel
                ? spec.edit.getValueLabel(control.field.value, form.getValues())
                : control.field.value,
              value: control.field.value,
            }
          : undefined
      }
      dependencyState={dependencyState}
      disabled={spec.edit.disabled}
      placeholder={spec.edit.placeholder}
      onSelect={(selected) => control.field.onChange(selected?.value || '')}
      render={({ label }) => {
        return <p>{label}</p>;
      }}
      emptyStateMessage='No se encontraron resultados'
      classNames={{
        button: 'h-8 px-3 text-sm truncate',
      }}
      loadOptions={(search) => {
        const ctx = form.getValues();
        return spec.edit.loadOptions(search, ctx);
      }}
    />
  );
};

const FieldEditControl = ({
  spec,
  control,
}: {
  spec: Exclude<FieldSpec, { edit: undefined | null }>;
  control: UseControllerReturn;
}) => {
  switch (spec.edit!.type) {
    case 'select': {
      return (
        <Select
          onValueChange={control.field.onChange}
          defaultValue={control.field.value}
        >
          <SelectTrigger size='sm'>
            <SelectValue placeholder='Selecciona una opción' />
          </SelectTrigger>
          <SelectContent>
            {spec.edit!.options.map((option, index) => {
              return (
                <SelectItem key={index} value={option.value}>
                  {option.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    }

    case 'number':
      return <NumberField {...control.field} size='sm' />;

    case 'text':
      return <Input {...control.field} size='sm' />;

    case 'textarea':
      return <Textarea {...control.field} size='sm' />;

    case 'check':
      return (
        <Switch
          size='sm'
          checked={control.field.value}
          onCheckedChange={control.field.onChange}
          ref={control.field.ref}
        />
      );

    case 'date':
      return <DateField control={control} spec={spec} />;

    case 'combobox':
      return <ComboboxField control={control} spec={spec} />;

    case 'select-awaited':
      return (
        <SelectAwaited
          classNames={{
            button: 'h-8 px-3 text-sm truncate',
          }}
          path={control.field.name}
          value={control.field.value?.toString()}
          ctrlField={control.field}
          field={{
            isFuture: false,
            label: spec.label,
            options: () => spec.edit!.options,
            withOptionSorted: false,
            virtualized: true,
            setter: (selected) => control.field.onChange(selected?.value || ''),
            resetOnFieldChange: () => spec.edit.resetOnFieldChange,
          }}
        />
      );
  }
};

const FieldEditRender = ({ field }: { field: FieldSpec }) => {
  const update = useSafeClaimUpdate();

  if (!field.edit) return <FieldViewRender field={field} />;

  if (field.edit.type === 'toggle')
    return (
      <Switch
        size='sm'
        checked={field.edit.checked}
        onCheckedChange={field.edit.onCheckedChange}
      />
    );

  return (
    <FormField
      control={update.form!.control}
      name={field.edit.formFieldName}
      defaultValue={field.defaultValue}
      render={(fieldControl) => {
        return (
          <FormItem>
            <FormControl>
              <FieldEditControl spec={field} control={fieldControl} />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

interface FieldsSegmentRenderProps {
  fields: FieldSpec[];
  segment: ClaimUpdateSegment;
}

const GroupedFieldsSegmentRender = ({
  className,
  children,
  ...props
}: FieldsSegmentRenderProps & {
  className?: string;
  children?: React.ReactNode;
}) => {
  return (
    <CardGroup
      className={cn(
        'col-span-1 bg-card border-none shadow-none p-0',
        className,
      )}
    >
      <CardGroupFields>
        <FieldsSegmentRender {...props} />
        {children}
      </CardGroupFields>
    </CardGroup>
  );
};

const FieldsSegmentRender = ({ fields, segment }: FieldsSegmentRenderProps) => {
  const update = useSafeClaimUpdate();

  const isEditingCurrentSegment =
    !!segment && update.editingSegment === segment;

  return fields
    .filter((field) => !field.hide)
    .map((field) => {
      return (
        <CardAttribute>
          <CardLabel>
            {field.label}
            {field.adornment}
          </CardLabel>
          {isEditingCurrentSegment ? (
            <FieldEditRender field={field} />
          ) : (
            <FieldViewRender field={field} />
          )}
        </CardAttribute>
      );
    });
};

// Mirrors the `sinister-info` step dropdowns (apps/customer-app): provincia and ciudad
// are selected from the same state/city catalogs used at claim creation time.
const buildOccurrenceStateOptions = (country?: string | null) =>
  (
    countryAndStates.find((c) => c.code === (country || DEFAULT_COUNTRY_CODE))
      ?.states || []
  ).map((state) => ({ label: state.name, value: state.code }));

const buildOccurrenceCityOptions = (state?: string | null) =>
  Array.from(
    argentinaCities[state as keyof typeof argentinaCities] ||
      paraguayCities[state as keyof typeof paraguayCities] ||
      [],
  )
    .sort()
    .map((city) => ({ label: toTitleCase(city), value: city }));

const filterComboboxOptions = <T extends { label: string }>(
  options: T[],
  search: string,
) =>
  options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

const Occurrence = ({
  data,
  className,
  segment,
}: {
  data: CreateClaimRequestBody['occurrence'];
  className?: string;
  segment: ClaimUpdateSegment;
}) => {
  const t = useTranslations();

  const update = useSafeClaimUpdate();

  const prefix = 'occurrence';

  const fields: FieldSpec[] = [
    {
      label: 'Departamento judicial',
      view: {
        type: 'accessor',
        accessor: () => data.judicial_department || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.judicial_department`,
      },
    },

    {
      label: 'Número causa penal',
      view: {
        type: 'accessor',
        accessor: () => data.criminal_case_number || '—',
      },
      edit: {
        type: 'number',
        formFieldName: `${prefix}.criminal_case_number`,
      },
    },

    {
      label: 'Número de Juzgado',
      view: {
        type: 'accessor',
        accessor: () => data.court || '—',
      },
      edit: {
        type: 'number',
        formFieldName: `${prefix}.court`,
      },
    },

    {
      label: 'Número de Secretaría',
      view: {
        type: 'accessor',
        accessor: () => data.clerks_office || '—',
      },
      edit: {
        type: 'number',
        formFieldName: `${prefix}.clerks_office`,
      },
    },

    {
      label: 'Comisaría',
      view: {
        type: 'accessor',
        accessor: () => data.police_station || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.police_station`,
      },
    },
  ];

  return <GroupedFieldsSegmentRender segment={segment} fields={fields} />;
};

// Sinister-location fields (provincia, ciudad, dirección, código postal). Rendered inline so
// they keep their position inside the "Datos del siniestro" card; when editable, each turns
// into the same control the `sinister-info` step uses (province/city comboboxes).
// `canEdit` gates editability: these fields can only be modified while the pre-claim has NOT yet
// been created in the provider (Rivadavia) — i.e. before external_claim_request_id is set. Once
// the pre-claim exists the location is frozen. When false they stay read-only even while the
// occurrence segment is in edit mode.
const OccurrenceLocationFields = ({
  data,
  canEdit = false,
  segment = 'occurrence',
}: {
  data: CreateClaimRequestBody['occurrence'];
  canEdit?: boolean;
  segment?: ClaimUpdateSegment;
}) => {
  const update = useSafeClaimUpdate();

  const prefix = 'occurrence';

  // Drive the ciudad field type off the currently-selected provincia: combobox when the
  // province has a known city catalog, free-text otherwise (same behavior as sinister-info).
  const selectedState = update.form?.watch(`${prefix}.state`) ?? data.state;
  const stateHasCities = buildOccurrenceCityOptions(selectedState).length > 0;

  const fields: FieldSpec[] = [
    {
      label: 'Provincia',
      view: {
        type: 'accessor',
        accessor: () =>
          data.country && data.state
            ? getStateLabel(data.country, data.state)
            : '—',
      },
      ...(canEdit
        ? {
            edit: {
              type: 'combobox',
              formFieldName: `${prefix}.state`,
              prefetch: true,
              dependencyState: [`${prefix}.country`],
              placeholder: 'Selecciona una provincia',
              loadOptions: async (search, ctx) =>
                filterComboboxOptions(
                  buildOccurrenceStateOptions(ctx.occurrence?.country),
                  search,
                ),
              getValueLabel: (value, ctx) =>
                getStateLabel(
                  ctx.occurrence?.country || DEFAULT_COUNTRY_CODE,
                  value,
                ),
            },
          }
        : {}),
    },
    {
      label: 'Ciudad',
      view: {
        type: 'accessor',
        accessor: () => data.city || '—',
      },
      ...(canEdit
        ? {
            edit: stateHasCities
              ? {
                  type: 'combobox',
                  formFieldName: `${prefix}.city`,
                  prefetch: true,
                  resetOnFieldChange: `${prefix}.state`,
                  dependencyState: [`${prefix}.state`],
                  placeholder: 'Selecciona una ciudad',
                  loadOptions: async (search, ctx) =>
                    filterComboboxOptions(
                      buildOccurrenceCityOptions(ctx.occurrence?.state),
                      search,
                    ),
                  getValueLabel: (value) => toTitleCase(value),
                }
              : {
                  type: 'text',
                  formFieldName: `${prefix}.city`,
                },
          }
        : {}),
    },
    {
      label: 'Dirección',
      view: {
        type: 'accessor',
        accessor: () => data.address || '—',
      },
      ...(canEdit
        ? {
            edit: {
              type: 'text',
              formFieldName: `${prefix}.address`,
            },
          }
        : {}),
    },
    {
      label: 'Código Postal',
      view: {
        type: 'accessor',
        accessor: () => data.zip_code || '—',
      },
      ...(canEdit
        ? {
            edit: {
              type: 'text',
              formFieldName: `${prefix}.zip_code`,
            },
          }
        : {}),
    },
  ];

  return <FieldsSegmentRender fields={fields} segment={segment} />;
};

const NaturalPerson = ({
  data,
  className,
  children,
  segment,
  prefix: _prefix = 'victim',
}: {
  data: NaturalPersonPopulated;
  className?: string;
  children: React.ReactNode;
  segment: ClaimUpdateSegment;
  prefix?: `victim` | `claimants.${number}`;
}) => {
  const t = useTranslations();

  const update = useSafeClaimUpdate();

  const isEditingSegment = update.editingSegment === segment;

  const prefix = `${_prefix}.natural_person` as const;

  const basicInfoFields: FieldSpec[] = [
    {
      label: 'Tipo',
      view: {
        type: 'literal',
        literal: 'Persona física',
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.first_name.label`),
      view: {
        type: 'accessor',
        accessor: () => data.first_name,
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.first_name`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.last_name.label`),
      view: {
        type: 'accessor',
        accessor: () => data.last_name,
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.last_name`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.birth_date.label`),
      view: {
        type: 'accessor',
        accessor: () =>
          data.birth_date
            ? format(new Date(data.birth_date), 'dd/MM/yyyy')
            : '—',
      },
      edit: {
        type: 'date',
        formFieldName: `${prefix}.birth_date`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.sex.label`),
      view: {
        type: 'accessor',
        accessor: () => t(`sex.${data.sex}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(SexTypes).map((sex) => {
          return {
            label: t(`sex.${sex}`),
            value: sex,
          };
        }),
        formFieldName: `${prefix}.sex`,
      },
    },
    {
      label: t(
        `claim.attributes.victim.natural_person.identification_type.label`,
      ),
      view: {
        type: 'accessor',
        accessor: () => t(`identification-type.${data.identification_type}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(IdentificationTypes).map((type) => {
          return {
            label: t(`identification-type.${type}`),
            value: type,
          };
        }),
        formFieldName: `${prefix}.identification_type`,
      },
    },
    {
      label: t(
        `claim.attributes.victim.natural_person.identification_value.label`,
      ),
      view: {
        type: 'accessor',
        accessor: () => data.identification_value,
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.identification_value`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.nationality.label`),
      view: {
        type: 'accessor',
        accessor: () => getCountryLabel(data.nationality),
      },
      edit: {
        type: 'select',
        formFieldName: `${prefix}.nationality`,
        options: countryAndStates.map((country) => {
          return {
            label: country.name,
            value: country.code,
          };
        }),
      },
    },
  ];

  const contactAndResidenceFields: FieldSpec[] = [
    {
      label: t(`claim.attributes.victim.natural_person.state.label`),
      view: {
        type: 'accessor',
        accessor: () => getStateLabel(DEFAULT_COUNTRY_CODE, data.state),
      },
      edit: {
        type: 'select',
        formFieldName: `${prefix}.state`,
        options:
          countryAndStates
            .find((country) => country.code === DEFAULT_COUNTRY_CODE)
            ?.states.map((state) => ({
              label: state.name,
              value: state.code,
            })) || [],
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.city.label`),
      view: {
        type: 'accessor',
        accessor: () => data.city || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.city`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.address_street.label`),
      view: {
        type: 'accessor',
        accessor: () => data.address_street || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.address_street`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.address_number.label`),
      view: {
        type: 'accessor',
        accessor: () => data.address_number || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.address_number`,
      },
    },
    {
      label: t(
        `claim.attributes.victim.natural_person.address_apartment.label`,
      ),
      view: {
        type: 'accessor',
        accessor: () => data.address_apartment || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.address_apartment`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.zip_code.label`),
      view: {
        type: 'accessor',
        accessor: () => data.zip_code || '—',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.zip_code`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.cellphone.label`),
      view: {
        type: 'accessor',
        accessor: () => data.cellphone || '',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.cellphone`,
      },
    },
    {
      label: t(`claim.attributes.victim.natural_person.email.label`),
      view: {
        type: 'accessor',
        accessor: () => data.email || '',
      },
      edit: {
        type: 'text',
        formFieldName: `${prefix}.email`,
      },
    },
  ];

  // Parent data only makes sense while the person was a minor at claim creation
  // time, and Natural rejects it outright on material-only claims. The section
  // reacts to the birth date being edited; hiding it never wipes what is stored.
  const editedBirthDate = update.form?.watch(`${prefix}.birth_date`);

  const isMinor = isMinorAt(
    (editedBirthDate as Date | undefined) ?? data.birth_date,
    update.data?.created_at,
  );

  const parentSlots = useMemo(() => {
    let nextIndex = data.parents.length;

    return nativeEnumToArray(PersonParentTypes).map((type) => {
      const index = data.parents.findIndex((parent) => parent.type === type);

      return {
        type,
        index: index >= 0 ? index : nextIndex++,
        parent: index >= 0 ? data.parents[index] : undefined,
      };
    });
  }, [data.parents]);

  const buildParentFields = useCallback(
    ({ type, index, parent }: (typeof parentSlots)[number]): FieldSpec[] => [
      {
        label: 'Familiar',
        view: {
          type: 'accessor',
          accessor: () => t(`victim_relationship.${type}`),
        },
        defaultValue: type,
        edit: {
          type: 'select',
          formFieldName: `${prefix}.parents.${index}.type`,
          options: nativeEnumToArray(PersonParentTypes).map((parentType) => ({
            label: t(`victim_relationship.${parentType}`),
            value: parentType,
          })),
        },
      },
      {
        label: 'Nombre',
        view: {
          type: 'accessor',
          accessor: () => parent?.first_name || '',
        },
        edit: {
          type: 'text',
          formFieldName: `${prefix}.parents.${index}.first_name`,
        },
      },
      {
        label: 'Apellido',
        view: {
          type: 'accessor',
          accessor: () => parent?.last_name || '',
        },
        edit: {
          type: 'text',
          formFieldName: `${prefix}.parents.${index}.last_name`,
        },
      },
      {
        label: t(`claim.attributes.victim.natural_person.birth_date.label`),
        view: {
          type: 'accessor',
          accessor: () =>
            parent?.birth_date
              ? format(new Date(parent.birth_date), 'dd/MM/yyyy')
              : '—',
        },
        edit: {
          type: 'date',
          formFieldName: `${prefix}.parents.${index}.birth_date`,
        },
      },
      {
        label: 'Documento',
        view: {
          type: 'accessor',
          accessor: () => parent?.identification || '',
        },
        edit: {
          type: 'text',
          formFieldName: `${prefix}.parents.${index}.identification`,
        },
      },
    ],
    [prefix, t],
  );

  const claimHasPersonDamage = (update.data?.damages || []).some(
    (damage) => damage.type === 'person',
  );

  const visibleParentSlots = isMinor
    ? parentSlots.filter(
        (slot) => slot.parent || (isEditingSegment && claimHasPersonDamage),
      )
    : [];

  return (
    <>
      <GroupedFieldsSegmentRender segment={segment} fields={basicInfoFields}>
        {children}
      </GroupedFieldsSegmentRender>

      <ClaimSummary.CardGroupLabel className='font-medium text-base_brand my-0'>
        <ClaimSummary.Icon>
          <IconInfoCircle className='w-4 h-4' />
        </ClaimSummary.Icon>
        Datos de contacto y Residencia
      </ClaimSummary.CardGroupLabel>
      <GroupedFieldsSegmentRender
        segment={segment}
        fields={contactAndResidenceFields}
      />

      {visibleParentSlots.length > 0 && (
        <>
          <Separator />
          <ClaimSummary.CardGroupLabel>Padres</ClaimSummary.CardGroupLabel>
          <div>
            {visibleParentSlots.map((slot) => (
              <GroupedFieldsSegmentRender
                key={slot.type}
                segment={segment}
                fields={buildParentFields(slot)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

const CardGroupSeparator = () => {
  return <Separator className='my-2' />;
};

const CardGroupFields = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('grid grid-cols-2 gap-3.5 py-1', className)}>
      {children}
    </div>
  );
};

const VehicleDamage = ({
  data,
  index,
}: {
  data: VehicleWithRelations;
  index: number;
}) => {
  const t = useTranslations();

  const update = useSafeClaimUpdate();

  const yearOptions = useMemo(() => {
    const optionsCount = 100;

    const currentYear = new Date().getFullYear();

    return new Array(optionsCount)
      .fill(null)
      .map((_, i) => currentYear - i)
      .map((year) => {
        return {
          label: year.toString(),
          value: year.toString(),
        };
      });
  }, []);

  const editedType = update.form?.watch(
    `damages.${index}.material.vehicle.type`,
  );

  const editedCategory = update.form?.watch(
    `damages.${index}.material.vehicle.category`,
  );

  // The insurer catalog only answers for some vehicle types. For the rest the
  // comboboxes are dead ends — they load nothing and cannot even show the value
  // the claimant typed — so make, model and version are free text.
  const usesVehicleCatalog =
    (editedCategory ?? data.category) === 'with_license_plate' &&
    isCatalogBackedVehicleType(editedType ?? data.type);

  const fields: FieldSpec[] = [
    {
      label: t(`claim.attributes.damages.material.vehicle.type.label`),
      view: {
        type: 'accessor',
        accessor: () => t(`vehicle.${data.type}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(VehicleTypes).map((type) => ({
          label: t(`vehicle.${type}`),
          value: type,
        })),
        formFieldName: `damages.${index}.material.vehicle.type`,
      },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.make.label`),
      view: {
        type: 'accessor',
        accessor: () => data.make,
      },
      edit: !usesVehicleCatalog
        ? {
            type: 'text',
            formFieldName: `damages.${index}.material.vehicle.make`,
          }
        : {
            type: 'combobox',
            formFieldName: `damages.${index}.material.vehicle.make`,
            prefetch: true,
            dependencyState: [`damages.${index}.material.vehicle.type`],
            disabled: false,
            placeholder: 'Busca una marca',
            loadOptions: async (search, ctx) => {
              const { damage } = getClaimDamage(ctx, 'material');

              if (
                !damage ||
                !damage.type ||
                damage.material.type !== 'vehicle' ||
                damage.material.vehicle.category !== 'with_license_plate'
              ) {
                return [];
              }

              const result = await update.fetchers!.listVehicleMake({
                search,
                type: damage.material.vehicle.type,
              });

              return result.options;
            },
          },
    },

    {
      label: t(`claim.attributes.damages.material.vehicle.model.label`),
      view: {
        type: 'accessor',
        accessor: () => data.model,
      },
      edit: !usesVehicleCatalog
        ? {
            type: 'text',
            formFieldName: `damages.${index}.material.vehicle.model`,
          }
        : {
            type: 'combobox',
            formFieldName: `damages.${index}.material.vehicle.model`,

            prefetch: true,
            resetOnFieldChange: `damages.${index}.material.vehicle.make`,
            dependencyState: [
              `damages.${index}.material.vehicle.type`,
              `damages.${index}.material.vehicle.make`,
            ],
            disabled: false,
            placeholder: 'Busca un modelo',
            loadOptions: async (search, ctx) => {
              const { damage } = getClaimDamage(ctx, 'material');

              if (
                !damage ||
                !damage.type ||
                damage.material.type !== 'vehicle' ||
                damage.material.vehicle.category !== 'with_license_plate' ||
                !damage.material.vehicle.make
              ) {
                return [];
              }

              const result = await update.fetchers!.listVehicleModel({
                make: damage.material.vehicle.make,
                search,
                type: damage.material.vehicle.type,
              });

              return result.options;
            },
          },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.year.label`),
      view: {
        type: 'accessor',
        accessor: () => data.year!.toString(),
      },
      edit: {
        type: 'select-awaited',
        formFieldName: `damages.${index}.material.vehicle.year`,
        options: yearOptions,
        virtualized: true,
        resetOnFieldChange: usesVehicleCatalog
          ? `damages.${index}.material.vehicle.model`
          : undefined,
      },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.version.label`),
      view: {
        type: 'accessor',
        accessor: () => data.version || '',
      },
      edit: !usesVehicleCatalog
        ? {
            type: 'text',
            formFieldName: `damages.${index}.material.vehicle.version`,
          }
        : {
            type: 'combobox',
            formFieldName: `damages.${index}.material.vehicle.version`,

            prefetch: true,
            resetOnFieldChange: `damages.${index}.material.vehicle.year`,
            dependencyState: [
              `damages.${index}.material.vehicle.type`,
              `damages.${index}.material.vehicle.make`,
              `damages.${index}.material.vehicle.model`,
              `damages.${index}.material.vehicle.year`,
            ],
            disabled: false,
            placeholder: 'Busca una versión',
            loadOptions: async (search, ctx) => {
              const { damage } = getClaimDamage(ctx, 'material');

              if (
                !damage ||
                !damage.type ||
                damage.material.type !== 'vehicle' ||
                damage.material.vehicle.category !== 'with_license_plate' ||
                !damage.material.vehicle.make ||
                !damage.material.vehicle.model ||
                !damage.material.vehicle.year
              ) {
                return [];
              }

              const result = await update.fetchers!.listVehicleVersion({
                type: damage.material.vehicle.type,
                make: damage.material.vehicle.make,
                model: damage.material.vehicle.model,
                year: damage.material.vehicle.year,
                search,
              });

              return result.options;
            },
          },
    },

    {
      label: t(`claim.attributes.damages.material.vehicle.use.label`),
      view: {
        type: 'accessor',
        accessor: () => t(`vehicle.use.${data.use}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(VehicleUseTypes).map((type) => ({
          label: t(`vehicle.use.${type}`),
          value: type,
        })),
        formFieldName: `damages.${index}.material.vehicle.use`,
      },
    },

    // Editable: guardar el segmento ya deja fila en el historial de estimaciones.
    // Ojo — si el vehículo matchea el catálogo de InfoAuto su precio pisa lo que
    // se cargue acá, y la fila registra el valor escrito, no el tipeado.
    {
      label: t(`claim.attributes.damages.material.vehicle.market_value.label`),
      view: {
        type: 'accessor',
        accessor: () =>
          data.market_value
            ? currencyFormat(data.market_value, DEFAULT_COUNTRY_CODE)
            : '',
      },
      edit: {
        type: 'number',
        formFieldName: `damages.${index}.material.vehicle.market_value`,
      },
    },

    {
      label: t(`claim.attributes.damages.material.vehicle.category.label`),
      view: {
        type: 'accessor',
        accessor: () => t(`vehicle.category.${data.category}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(VehicleCategoryTypes).map((type) => ({
          label: t(`vehicle.category.${type}`),
          value: type,
        })),
        formFieldName: `damages.${index}.material.vehicle.category`,
      },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.license_plate.label`),
      view: {
        type: 'accessor',
        accessor: () => data.license_plate || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.vehicle.license_plate`,
      },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.chassis.label`),
      view: {
        type: 'accessor',
        accessor: () => data.chassis || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.vehicle.chassis`,
      },
    },
    {
      label: t(`claim.attributes.damages.material.vehicle.engine.label`),
      view: {
        type: 'accessor',
        accessor: () => data.engine || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.vehicle.engine`,
      },
    },
  ];

  return <FieldsSegmentRender fields={fields} segment={`damages`} />;
};

const RealPropertyDamage = ({
  data,
}: {
  data: MaterialDamageWithRelations;
}) => {
  const country = countryAndStates.find(
    (c) => c.code === data.real_property!.country,
  );

  const state = countryAndStates
    .find((c) => c.code === data.real_property!.country)
    ?.states?.find((s) => s.code === data.real_property!.state);

  const t = useTranslations();

  return (
    <>
      {country && (
        <CardAttribute>
          <CardLabel>País</CardLabel>
          <CardValue>{country.name}</CardValue>
        </CardAttribute>
      )}

      {state && (
        <CardAttribute>
          <CardLabel>Provincia</CardLabel>
          <CardValue>{state.name}</CardValue>
        </CardAttribute>
      )}

      <CardAttribute>
        <CardLabel>Ciudad</CardLabel>
        <CardValue>{data.real_property!.city}</CardValue>
      </CardAttribute>

      <CardAttribute>
        <CardLabel>Dirección</CardLabel>
        <CardValue>{data.real_property!.address}</CardValue>
      </CardAttribute>

      <CardAttribute>
        <CardLabel>Código Postal</CardLabel>
        <CardValue>{data.real_property!.zip_code}</CardValue>
      </CardAttribute>

      <CardAttribute>
        <CardLabel>Tipo</CardLabel>
        <CardValue>
          {t(`real_property.type.${data.real_property!.type}`)}
        </CardValue>
      </CardAttribute>

      <CardAttribute>
        <CardLabel>Propiedad</CardLabel>
        <CardValue>
          {t(`real_property.ownership.${data.real_property!.ownership}`)}
        </CardValue>
      </CardAttribute>
    </>
  );
};

const PersonDamageDetailsDataTable = ({
  details,
}: {
  details: PersonDamageDetail[];
}) => {
  const t = useTranslations();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Daño</TableHead>
          <TableHead>Severidad</TableHead>
          <TableHead className='text-right'>Partes</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {details.map((detail) => (
          <TableRow key={detail.id}>
            <TableCell className='font-medium'>
              {t(`injuries.${detail.damage}.label`)}
            </TableCell>
            <TableCell className='font-medium'>
              {t(`severities.${detail.severity}`)}
            </TableCell>
            <TableCell className='text-right'>
              {detail.body_parts && detail.body_parts?.length > 0
                ? detail.body_parts
                    .map((part) => t(`fractures.${part}`))
                    .join(', ')
                : ''}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const ROOT_TRANSLATION_NAMESPACE = '';

type DamageDriver = NonNullable<ClaimDamageWithRelations['person']>['driver'];

// The provider rejects a conductor whose name is too long, so the insurer must be
// able to shorten it here instead of leaving the claim stuck.
const DamageDriverAttributes = ({
  driver,
  formFieldPrefix,
}: {
  driver: DamageDriver | undefined;
  formFieldPrefix:
    | `damages.${number}.person.driver`
    | `damages.${number}.material.driver`;
}) => {
  const t = useTranslations('claim.attributes.damages.material.vehicle.driver');

  if (!driver) return null;

  const fields: FieldSpec[] = [
    {
      label: t('first_name.label'),
      view: { type: 'accessor', accessor: () => driver.first_name || '' },
      edit: { type: 'text', formFieldName: `${formFieldPrefix}.first_name` },
    },
    {
      label: t('last_name.label'),
      view: { type: 'accessor', accessor: () => driver.last_name || '' },
      edit: { type: 'text', formFieldName: `${formFieldPrefix}.last_name` },
    },
    {
      label: t('cellphone.label'),
      view: { type: 'accessor', accessor: () => driver.cellphone || '' },
      edit: { type: 'text', formFieldName: `${formFieldPrefix}.cellphone` },
    },
    {
      label: t('ownership.label'),
      view: {
        type: 'accessor',
        accessor: () => t(`ownership.value.${driver.ownership}`),
      },
    },
  ];

  return <FieldsSegmentRender fields={fields} segment='damages' />;
};

const PersonDamage = ({
  data,
  index,
  insurerName,
}: {
  data: ClaimDamageWithRelations;
  index: number;
  insurerName?: string;
}) => {
  const t = useTranslations(ROOT_TRANSLATION_NAMESPACE, '');

  const fields: FieldSpec[] = [
    {
      label: t(`claim.attributes.damages.person.victim_role.label`),
      view: {
        type: 'accessor',
        accessor: () =>
          data.person!.victim_role
            ? t(`victim-injuries-roles.${data.person!.victim_role}`, {
                insurerName,
              })
            : '',
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(VictimRoleTypes).map((type) => ({
          label: t(`victim-injuries-roles.${type}`, { insurerName }),
          value: type,
        })),
        formFieldName: `damages.${index}.person.victim_role`,
      },
    },
    {
      label: t(
        'claim.attributes.damages.person.policyholder_relationship.label',
      ),
      view: {
        type: 'accessor',
        accessor: () =>
          t(
            `local-insured-relationships.${
              data.person!.policyholder_relationship
            }`,
          ),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(PolicyHolderRelationshipTypes).map(
          (type) => ({
            label: t(`local-insured-relationships.${type}`),
            value: type,
          }),
        ),
        formFieldName: `damages.${index}.person.policyholder_relationship`,
      },
    },

    {
      label: t(
        'claim.attributes.damages.person.policyholder_relationship_other.label',
      ),
      view: {
        type: 'accessor',
        accessor: () => data.person!.policyholder_relationship_other || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.person.policyholder_relationship_other`,
      },
    },

    {
      label: t('claim.attributes.damages.person.health_insurance_fund.label'),
      view: {
        type: 'accessor',
        accessor: () => data.person!.health_insurance_fund || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.person.health_insurance_fund`,
      },
    },
    {
      label: t(
        `claim.attributes.damages.person.treatment_worker_compensation_insurer.label`,
      ),
      view: {
        type: 'accessor',
        accessor: () =>
          data.person!.treatment_worker_compensation_insurer || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.person.treatment_worker_compensation_insurer`,
      },
    },
    {
      label: t(
        'claim.attributes.damages.person.treatment_medical_institution.label',
      ),
      view: {
        type: 'accessor',
        accessor: () => data.person!.treatment_medical_institution || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.person.treatment_medical_institution`,
      },
    },
    {
      label: t('claim.attributes.damages.person.surgery_description.label'),
      view: {
        type: 'accessor',
        accessor: () => data.person!.surgery_description || '',
      },
      edit: {
        type: 'textarea',
        formFieldName: `damages.${index}.person.surgery_description`,
      },
    },
  ];

  return (
    <>
      <CardAttribute>
        <CardLabel>Subtipo</CardLabel>
        <CardValue>{t(`damages.${data.person!.type}`)}</CardValue>
      </CardAttribute>

      <FieldsSegmentRender fields={fields} segment='damages' />

      <DamageDriverAttributes
        driver={data.person?.driver}
        formFieldPrefix={`damages.${index}.person.driver`}
      />

      {data.person!.details?.length > 0 && (
        <div className='col-span-full'>
          <Separator className='my-4' />
          <ClaimSummary.Header>
            <ClaimSummary.CardGroupLabel>
              <ClaimSummary.Icon>
                <IconFall />
              </ClaimSummary.Icon>
              Lesiones
            </ClaimSummary.CardGroupLabel>
          </ClaimSummary.Header>
          <PersonDamageDetailsDataTable details={data.person!.details} />
        </div>
      )}
    </>
  );
};

const MaterialDamageTypeAttributes = ({
  data,
  index,
}: {
  data: ClaimDamageWithRelations;
  index: number;
}) => {
  const t = useTranslations();

  switch (data.material!.type) {
    case 'vehicle':
      return <VehicleDamage index={index} data={data.material!.vehicle!} />;

    case 'real_property':
      return <RealPropertyDamage data={data.material!} />;

    default:
      return null;
  }
};

const MaterialDamageAttributes = ({
  data,
  index,
  searchEntity,
}: {
  data: ClaimDamageWithRelations;
  index: number;
  searchEntity?: (
    query: SearchEntityRequestQuery,
  ) => Promise<SearchEntityResponseBody> | undefined;
}) => {
  const t = useTranslations();

  const update = useSafeClaimUpdate();

  const isEditingDamages = update.editingSegment === 'damages';

  const watchedInsurance = update.form?.watch(
    `damages.${index}.material.insurance`,
  );

  const insurance = isEditingDamages
    ? watchedInsurance
    : data.material?.insurance;

  const hasInsurance = !!insurance;

  const isFranchiseCoverage =
    insurance?.coverage === 'all_risks_with_franchise';

  const hasPlan = data.material && data.material.insurance?.plan;
  const hasCoverageAmount =
    data.material && data.material.insurance?.coverage_amount;

  const toggleInsurance = useCallback(
    (checked: boolean) => {
      if (!checked) {
        update.form?.setValue(`damages.${index}.material.insurance`, null);
        update.form?.clearErrors(`damages.${index}.material.insurance`);
        return;
      }

      // Writing a leaf creates the parent object, so the fields show up empty and
      // Zod demands the required ones when saving.
      update.form?.setValue(`damages.${index}.material.insurance.insurer`, '');
    },
    [update.form, index],
  );

  const fields: FieldSpec[] = [
    {
      label: t(
        'claim.attributes.damages.material.insurance.has_insurance.label',
      ),
      view: {
        type: 'accessor',
        accessor: () =>
          t(
            `claim.attributes.damages.material.insurance.has_insurance.value.${hasInsurance}`,
          ),
      },
      edit: {
        type: 'toggle',
        checked: hasInsurance,
        onCheckedChange: toggleInsurance,
      },
    },
    {
      label: 'Aseguradora',
      hide: !hasInsurance,
      view: {
        type: 'accessor',
        accessor: () => data.material?.insurance?.insurer || '',
      },
      edit: {
        type: 'combobox',
        formFieldName: `damages.${index}.material.insurance.insurer`,
        prefetch: true,
        disabled: !searchEntity,
        placeholder: 'Buscar aseguradora',
        loadOptions: async (search) => {
          if (!searchEntity) return [];

          const response = await searchEntity({
            entity: 'insurer',
            withPagination: false,
          });
          if (!response) return [];

          return response.results
            .filter((insurer) =>
              insurer.name.toLowerCase().includes(search.toLowerCase()),
            )
            .map((insurer) => ({ label: insurer.name, value: insurer.name }));
        },
      },
    },

    {
      label: 'Cobertura',
      hide: !hasInsurance,
      view: {
        type: 'accessor',
        accessor: () =>
          data.material?.insurance?.coverage
            ? t(`coverages.${data.material.insurance.coverage}`)
            : '',
      },
      edit: {
        type: 'select',
        formFieldName: `damages.${index}.material.insurance.coverage`,
        options: nativeEnumToArray(CoverageTypes).map((type) => ({
          label: t(`coverages.${type}`),
          value: type,
        })),
      },
    },
    {
      label: 'Otra cobertura',
      hide: !hasInsurance || !data.material?.insurance?.other_coverage,
      view: {
        type: 'accessor',
        accessor: () => data.material?.insurance?.other_coverage || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.insurance.other_coverage`,
      },
    },
    {
      label: 'Número de Póliza',
      hide: !hasInsurance,
      view: {
        type: 'accessor',
        accessor: () => data.material?.insurance?.policy_number || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.insurance.policy_number`,
      },
    },
    {
      label: 'Monto franquicia',
      hide: !hasInsurance || !isFranchiseCoverage,
      view: {
        type: 'accessor',
        accessor: () =>
          data.material?.insurance?.franchise_amount
            ? currencyFormat(
                data.material.insurance.franchise_amount,
                undefined,
                true,
              )
            : '',
      },
      edit: {
        type: 'number',
        formFieldName: `damages.${index}.material.insurance.franchise_amount`,
      },
    },
    {
      label: 'Plan',
      hide: !hasInsurance || !hasPlan,
      view: {
        type: 'accessor',
        accessor: () => (data.material && data.material.insurance?.plan) || '',
      },
      edit: {
        type: 'text',
        formFieldName: `damages.${index}.material.insurance.plan`,
      },
    },
    {
      label: 'Monto cobertura',
      hide: !hasInsurance || !hasCoverageAmount,
      view: {
        type: 'accessor',
        accessor: () =>
          data.material && data.material.insurance?.coverage_amount
            ? currencyFormat(
                data.material.insurance?.coverage_amount,
                undefined,
                true,
              )
            : '',
      },
      edit: {
        type: 'number',
        formFieldName: `damages.${index}.material.insurance.coverage_amount`,
      },
    },
  ];

  return <FieldsSegmentRender fields={fields} segment='damages' />;
};

const MaterialDamage = ({
  data,
  index,
  searchEntity,
}: {
  data: ClaimDamageWithRelations;
  index: number;
  searchEntity?: (
    query: SearchEntityRequestQuery,
  ) => Promise<SearchEntityResponseBody> | undefined;
}) => {
  const t = useTranslations();

  return (
    <>
      <CardAttribute>
        <CardLabel>Subtipo</CardLabel>
        <CardValue>{t(`materialDamage.${data.material!.type}`)}</CardValue>
      </CardAttribute>

      <MaterialDamageTypeAttributes index={index} data={data} />

      <DamageDriverAttributes
        driver={data.material?.driver}
        formFieldPrefix={`damages.${index}.material.driver`}
      />

      {/* Rendered even without an Insurance row: "¿Posee seguro?" must be visible and
          editable when the third party declared "sin seguro" (PIRAMID-708). */}
      <MaterialDamageAttributes
        data={data}
        index={index}
        searchEntity={searchEntity}
      />
    </>
  );
};

const RecoveryDamage = ({ data }: { data: ClaimDamageWithRelations }) => {
  const t = useTranslations();

  const state = countryAndStates
    .find((c) => c.code === DEFAULT_COUNTRY_CODE)
    ?.states?.find((s) => s.code === data.recovery!.institution!.state);

  const damagedPerson = data.recovery!.damaged_person;

  return (
    <>
      <CardAttribute>
        <CardLabel>Institución</CardLabel>
        <CardValue>{data.recovery!.institution.social_reason}</CardValue>
      </CardAttribute>
      <CardAttribute>
        <CardLabel>Tipo</CardLabel>
        <CardValue>
          {t(`legalPersonType.${data.recovery!.institution.type}`)}
        </CardValue>
      </CardAttribute>
      {state && (
        <CardAttribute>
          <CardLabel>Provincia</CardLabel>
          <CardValue>{state.name}</CardValue>
        </CardAttribute>
      )}

      <CardAttribute>
        <CardLabel>Ciudad</CardLabel>
        <CardValue>{data.recovery!.institution.city}</CardValue>
      </CardAttribute>
      <CardAttribute>
        <CardLabel>Dirección</CardLabel>
        <CardValue>{data.recovery!.institution.address}</CardValue>
      </CardAttribute>
      <CardAttribute>
        <CardLabel>Código Postal</CardLabel>
        <CardValue>{data.recovery!.institution.zip_code}</CardValue>
      </CardAttribute>
      <CardAttribute>
        <CardLabel>CUIT</CardLabel>
        <CardValue>{data.recovery!.institution.cuit}</CardValue>
      </CardAttribute>
    </>
  );
};

// The claiming institution is the claim's victim, so the person it claims for
// is shown in that same card instead of among the damages.
const RecoveryDamagedPerson = ({ damages }: { damages: Damage[] }) => {
  const t = useTranslations();

  const damagedPerson = damages.find((damage) => damage.type === 'recovery')
    ?.recovery?.damaged_person;

  const state = countryAndStates
    .find((c) => c.code === DEFAULT_COUNTRY_CODE)
    ?.states?.find((s) => s.code === damagedPerson?.state);

  if (!damagedPerson) return null;

  const fields: FieldSpec[] = [
    {
      label: 'Tipo',
      view: { type: 'literal', literal: 'Persona física' },
    },
    {
      label: 'Nombre',
      view: { type: 'accessor', accessor: () => damagedPerson.first_name },
    },
    {
      label: 'Apellido',
      view: { type: 'accessor', accessor: () => damagedPerson.last_name },
    },
    {
      label: 'Tipo de documento',
      view: {
        type: 'accessor',
        accessor: () =>
          t(`identification-type.${damagedPerson.identification_type}`),
      },
    },
    {
      label: 'Documento',
      view: {
        type: 'accessor',
        accessor: () => damagedPerson.identification_value,
      },
    },
    {
      label: 'Fecha de nacimiento',
      view: {
        type: 'accessor',
        accessor: () =>
          damagedPerson.birth_date
            ? format(new Date(damagedPerson.birth_date), 'dd/MM/yyyy')
            : '—',
      },
    },
    {
      label: 'Nacionalidad',
      view: {
        type: 'accessor',
        accessor: () => damagedPerson.nationality || '—',
      },
    },
    {
      label: 'Provincia',
      view: { type: 'accessor', accessor: () => state?.name || '—' },
    },
    {
      label: 'Ciudad',
      view: { type: 'accessor', accessor: () => damagedPerson.city || '—' },
    },
    {
      label: 'Dirección',
      view: { type: 'accessor', accessor: () => damagedPerson.address || '—' },
    },
    {
      label: 'Código Postal',
      view: { type: 'accessor', accessor: () => damagedPerson.zip_code || '—' },
    },
    {
      label: 'Celular',
      view: {
        type: 'accessor',
        accessor: () => damagedPerson.cellphone || '—',
      },
    },
    {
      label: 'Correo',
      view: { type: 'accessor', accessor: () => damagedPerson.email || '—' },
    },
  ];

  return (
    <>
      <CardGroupSeparator />
      <CardAttribute className='col-span-full'>
        <CardLabel>Damnificado por el que se reclama</CardLabel>
      </CardAttribute>
      <GroupedFieldsSegmentRender segment='victim' fields={fields} />
    </>
  );
};

const Icon = ({ children }: { children: React.ReactNode }) => {
  return (
    <span className='[&>svg]:w-4 [&>svg]:h-4 [&>svg]:mr-1'>{children}</span>
  );
};

const LocationAttributes = ({
  data,
}: {
  data: Pick<
    ClaimWithRelations,
    | 'desired_support_location_state'
    | 'desired_support_location_state_municipality'
  >;
}) => {
  const state = countryAndStates
    .find((c) => c.code === DEFAULT_COUNTRY_CODE)
    ?.states.find((s) => s.code === data.desired_support_location_state);

  return (
    <CardAttribute>
      <CardLabel>Ubicación</CardLabel>
      <CardValue>
        {state?.name}{' '}
        {data.desired_support_location_state_municipality
          ? ` - ${data.desired_support_location_state_municipality}`
          : ''}
      </CardValue>
    </CardAttribute>
  );
};

const DamageAttributes = ({
  damage,
  index,
  searchEntity,
  insurerName,
}: {
  damage: ClaimDamageWithRelations;
  index: number;
  searchEntity?: (
    query: SearchEntityRequestQuery,
  ) => Promise<SearchEntityResponseBody> | undefined;
  insurerName?: string;
}) => {
  switch (damage.type) {
    case 'material':
      return (
        <MaterialDamage
          index={index}
          data={damage}
          searchEntity={searchEntity}
        />
      );

    case 'recovery':
      return <RecoveryDamage data={damage} />;

    case 'person':
      return (
        <PersonDamage index={index} data={damage} insurerName={insurerName} />
      );
  }
};

// Flags the victim as a minor at the time the claim was created, which is what
// drives whether parent data belongs on the claim at all.
const VictimMinorBadge = () => {
  const update = useSafeClaimUpdate();

  const editedBirthDate = update.form?.watch(
    'victim.natural_person.birth_date',
  );

  const birthDate =
    (editedBirthDate as Date | undefined) ??
    update.data?.victim?.natural_person?.birth_date;

  if (!isMinorAt(birthDate, update.data?.created_at)) return null;

  return <Badge className='font-medium'>Menor de edad</Badge>;
};

const Header = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='flex flex-col md:flex-row justify-between md:items-center gap-2 min-h-10'>
      {children}
    </div>
  );
};

export const ClaimSummary = ({ children }: { children: React.ReactNode }) => {
  return <div>{children}</div>;
};

/**
 * Marks whether the declared relationship holds up against the three
 * identities a claim can involve: the claimant, the victim and the registered
 * person the claim belongs to. It reads the form as it is edited, so
 * correcting the relationship turns the mark green before saving.
 */
const VictimRelationshipConsistency = ({
  claimant,
  index,
}: {
  claimant: Claimant;
  index: number;
}) => {
  const update = useSafeClaimUpdate();

  if (!update.data || !update.form) return null;

  return (
    <VictimRelationshipMark
      claimant={claimant}
      index={index}
      claim={update.data}
      control={update.form.control}
    />
  );
};

const VictimRelationshipMark = ({
  claimant,
  index,
  claim,
  control,
}: {
  claimant: Claimant;
  index: number;
  claim: NonUndefined<ClaimUpdateContextType['data']>;
  control: Control<UpdateClaimRequestBody>;
}) => {
  const t = useTranslations();

  const [editedClaimants, editedVictim] = useWatch({
    control,
    name: ['claimants', 'victim'],
  });

  const editedClaimant = editedClaimants?.[index];

  const violations = validateClaimantVictimConsistency({
    victimRelationship:
      editedClaimant?.victim_relationship ?? claimant.victim_relationship,
    claimant:
      consistencyIdentityOf(editedClaimant) ??
      consistencyIdentityOf(claimant) ??
      {},
    victim:
      consistencyIdentityOf(editedVictim) ??
      consistencyIdentityOf(claim.victim),
    ownerEmail: claim.owner?.email,
  });

  const consistent = !violations.length;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          {consistent ? (
            <IconCircleCheck className='w-4 h-4 text-green-400 ml-1' />
          ) : (
            <IconAlertCircle className='w-4 h-4 text-orange-400 ml-1' />
          )}
        </TooltipTrigger>
        <TooltipContent className='max-w-72 font-normal text-stone-700'>
          {consistent ? (
            t('claimant_victim_violation.consistent')
          ) : (
            <>
              <p>{t('claimant_victim_violation.for_declared_relationship')}</p>
              <ul className='list-disc space-y-1 pl-4'>
                {violations.map((violation) => (
                  <li key={violation}>
                    {t(`claimant_victim_violation.${violation}`)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ClaimantAdditionalAttributes = ({
  claimant,
  index,
}: {
  claimant: Claimant;
  index: number;
}) => {
  const t = useTranslations();

  const fields: FieldSpec[] = [
    {
      label: t(`claim.attributes.claimants.victim_relationship.label`),
      adornment: (
        <VictimRelationshipConsistency claimant={claimant} index={index} />
      ),
      view: {
        type: 'accessor',
        accessor: () =>
          t(`victim_relationship.${claimant.victim_relationship}`),
      },
      edit: {
        type: 'select',
        options: nativeEnumToArray(VictimRelationshipTypes).map((type) => ({
          label: t(`victim_relationship.${type}`),
          value: type,
        })),
        formFieldName: `claimants.${index}.victim_relationship`,
      },
    },
    {
      label: t(`claim.attributes.claimants.victim_relationship_other.label`),
      view: {
        type: 'accessor',
        accessor: () => claimant.victim_relationship_other || '',
      },
      edit: {
        type: 'text',
        formFieldName: `claimants.${index}.victim_relationship_other`,
      },
    },
  ];

  return <FieldsSegmentRender fields={fields} segment='claimants' />;
};

const ClaimantCard = ({
  claimant,
  index,
}: {
  claimant: Claimant;
  index: number;
}) => {
  const t = useTranslations();

  if (claimant.type !== 'natural_person') return null;

  return (
    <ClaimSummary.NaturalPerson
      segment={'claimants'}
      data={claimant.natural_person!}
      prefix={`claimants.${index}`}
    >
      <ClaimantAdditionalAttributes claimant={claimant} index={index} />
    </ClaimSummary.NaturalPerson>
  );
};

type Claimant = FetchClaimDetailResponseBody['claimants'][number];
type Damage = FetchClaimDetailResponseBody['damages'][number];
type OwnershipTransferHistoryEntry =
  FetchClaimDetailResponseBody['ownership_transfers'][number];

const DamageEditableFields = ({
  damage,
  index,
}: {
  damage: Damage;
  index: number;
}) => {
  const fields: FieldSpec[] = [
    {
      label: '¿Fue arreglado?',
      view: {
        type: 'accessor',
        accessor: () => (damage.fixed ? 'Si' : 'No'),
      },
      edit: {
        type: 'check',
        formFieldName: `damages.${index}.fixed`,
      },
    },
  ];

  return <FieldsSegmentRender fields={fields} segment='damages' />;
};

const Damages = ({
  damages,
  searchEntity,
  insurerName,
}: {
  damages: Damage[];
  searchEntity?: (
    query: SearchEntityRequestQuery,
  ) => Promise<SearchEntityResponseBody> | undefined;
  insurerName?: string;
}) => {
  // The recovery damage is not listed: it is shown in the victim card. The
  // original index is kept because editing targets `damages.${index}`.
  const visibleDamages = useMemo(() => {
    const entries = damages.map((damage, index) => ({ damage, index }));

    const withoutRecovery = entries.filter(
      ({ damage }) => damage.type !== 'recovery',
    );

    return withoutRecovery.length ? withoutRecovery : entries;
  }, [damages]);

  const [damageIndex, setDamageIndex] = useState(visibleDamages[0]?.index ?? 0);

  const damage = damages[damageIndex];

  const t = useTranslations();

  const update = useSafeClaimUpdate();

  return (
    <ClaimSummary.CardGroup className='col-span-1'>
      <ClaimSummary.Header>
        <ClaimSummary.CardGroupLabel>
          <ClaimSummary.Icon>
            <IconAlertTriangle />
          </ClaimSummary.Icon>
          Daños
        </ClaimSummary.CardGroupLabel>
        <div className='flex flex-row items-center space-x-2'>
          <Select
            onValueChange={(value) => setDamageIndex(Number(value))}
            defaultValue={damageIndex.toString()}
          >
            <SelectTrigger size='sm' className='w-48'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleDamages.map(({ damage: c, index }) => {
                return (
                  <SelectItem key={index} value={index.toString()}>
                    {t(`damages.${c.type}`)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {update.enabled && (
            <EditClaimButton data={update.data!} segment='damages' />
          )}
        </div>
      </ClaimSummary.Header>
      <ClaimSummary.CardGroupSeparator />
      <ClaimSummary.CardGroup className='border-none shadow-none p-0'>
        <ClaimSummary.CardGroupFields>
          {damage?.description && (
            <ClaimSummary.CardAttribute>
              <ClaimSummary.CardLabel>
                Descripción de daños
              </ClaimSummary.CardLabel>
              <ClaimSummary.CardValue>
                {damage?.description}
              </ClaimSummary.CardValue>
            </ClaimSummary.CardAttribute>
          )}

          <DamageEditableFields damage={damage} index={damageIndex} />

          <ClaimSummary.DamageAttributes
            index={damageIndex}
            damage={damage}
            searchEntity={searchEntity}
            insurerName={insurerName}
          />
        </ClaimSummary.CardGroupFields>
      </ClaimSummary.CardGroup>
    </ClaimSummary.CardGroup>
  );
};

const Claimants = ({
  claimants,
  ownerEmail,
  ownershipTransfers,
}: {
  claimants: Claimant[];
  ownerEmail?: string | null;
  ownershipTransfers?: OwnershipTransferHistoryEntry[];
}) => {
  const t = useTranslations();

  const principalClaimantIndex = useMemo(() => {
    //Transferred claims created before the roles backfill keep several rows
    //with the claimant role: the one synced with the claim owner is the
    //current principal
    if (ownerEmail) {
      const ownerRoleIndex = claimants.findIndex(
        (claimant) =>
          claimant.role === 'claimant' &&
          claimant.natural_person?.email === ownerEmail,
      );

      if (ownerRoleIndex >= 0) return ownerRoleIndex;
    }

    const roleIndex = claimants.findIndex(
      (claimant) => claimant.role === 'claimant',
    );

    if (roleIndex >= 0) return roleIndex;

    if (ownerEmail)
      return claimants.findIndex(
        (claimant) => claimant.natural_person?.email === ownerEmail,
      );

    return -1;
  }, [claimants, ownerEmail]);

  // Only the current principal claimant (synced with the owner) plus heirs are
  // listed: legacy transferred claims accumulated stale `claimant` rows for
  // past owners, whose history now lives in the ownership transfers section.
  const displayedClaimants = useMemo(() => {
    const entries: { claimant: Claimant; index: number }[] = [];

    if (principalClaimantIndex >= 0)
      entries.push({
        claimant: claimants[principalClaimantIndex],
        index: principalClaimantIndex,
      });

    claimants.forEach((claimant, index) => {
      if (index !== principalClaimantIndex && claimant.role === 'heir')
        entries.push({ claimant, index });
    });

    return entries.length
      ? entries
      : claimants.map((claimant, index) => ({ claimant, index }));
  }, [claimants, principalClaimantIndex]);

  const [selectedClaimantIndex, setSelectedClaimantIndex] = useState<
    number | null
  >(null);

  const claimantIndex =
    selectedClaimantIndex ?? displayedClaimants[0]?.index ?? 0;

  const claimant = claimants[claimantIndex];

  const buildLabel = useCallback(
    (claimant: Claimant): string => {
      const name =
        claimant.type === 'natural_person'
          ? `${claimant.natural_person?.first_name} ${claimant.natural_person?.last_name}`
          : claimant.legal_person?.social_reason!;

      // Only heirs carry a tag; the principal claimant shows plain since the
      // card itself is already the "Reclamantes" section.
      return claimant.role === 'heir'
        ? `${name} (${t('claim.attributes.claimants.role.value.heir')})`
        : name;
    },
    [t],
  );

  const update = useSafeClaimUpdate();

  return (
    <ClaimSummary.CardGroup className='col-span-1'>
      <ClaimSummary.Header>
        <ClaimSummary.CardGroupLabel>
          <ClaimSummary.Icon>
            <IconUserExclamation />
          </ClaimSummary.Icon>
          Reclamantes
        </ClaimSummary.CardGroupLabel>
        <div className='flex flex-row items-center space-x-2'>
          <Select
            onValueChange={(value) => setSelectedClaimantIndex(Number(value))}
            value={claimantIndex.toString()}
          >
            <SelectTrigger size='sm' className='w-48 truncate'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {displayedClaimants.map(({ claimant, index }) => {
                return (
                  <SelectItem key={claimant.id} value={index.toString()}>
                    {buildLabel(claimant)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {update.enabled && (
            <EditClaimButton
              data={update.data!}
              segment='claimants'
              itemIndex={claimantIndex}
            />
          )}
        </div>
      </ClaimSummary.Header>

      <ClaimSummary.CardGroupSeparator />
      <ClaimSummary.CardGroupFields className={cn('grid grid-cols-1')}>
        <ClaimantCard index={claimantIndex} claimant={claimant} />
      </ClaimSummary.CardGroupFields>

      {!!ownershipTransfers?.length && (
        <>
          <ClaimSummary.CardGroupSeparator />
          <ClaimSummary.CardGroupFields className={cn('grid grid-cols-1')}>
            <ClaimSummary.CardAttribute>
              <ClaimSummary.CardLabel>
                {t('claim.attributes.claimants.ownership_history.label')}
              </ClaimSummary.CardLabel>
              <div className='flex flex-col space-y-1'>
                {ownershipTransfers.map((transfer) => (
                  <div
                    key={transfer.id}
                    className='py-0.5 grid grid-cols-2 gap-6'
                  >
                    <span className='col-span-1'>
                      {buildUserFullName(transfer.source_user)} {'→'}{' '}
                      {buildUserFullName(transfer.destination_user)}
                    </span>
                    {transfer.accepted_at && (
                      <span className='text-muted-foreground col-span-1'>
                        {format(new Date(transfer.accepted_at), 'dd/MM/yyyy')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </ClaimSummary.CardAttribute>
          </ClaimSummary.CardGroupFields>
        </>
      )}
    </ClaimSummary.CardGroup>
  );
};

export const CardList = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>{children}</div>
  );
};

ClaimSummary.CardList = CardList;
ClaimSummary.Damages = Damages;
ClaimSummary.Claimants = Claimants;
ClaimSummary.DamageAttributes = DamageAttributes;
ClaimSummary.LocationAttributes = LocationAttributes;
ClaimSummary.PersonDamage = PersonDamage;
ClaimSummary.Claimant = Claimant;
ClaimSummary.Victim = Claimant;
ClaimSummary.Icon = Icon;
ClaimSummary.MaterialDamage = MaterialDamage;
ClaimSummary.RecoveryDamage = RecoveryDamage;
ClaimSummary.RecoveryDamagedPerson = RecoveryDamagedPerson;
ClaimSummary.NaturalPerson = NaturalPerson;
ClaimSummary.LegalPerson = LegalPerson;
ClaimSummary.CardAttribute = CardAttribute;
ClaimSummary.CardLabel = CardLabel;
ClaimSummary.CardValue = CardValue;
ClaimSummary.CardGroupFields = CardGroupFields;
ClaimSummary.CardGroup = CardGroup;
ClaimSummary.CardGroupLabel = CardGroupLabel;
ClaimSummary.CardGroupSeparator = CardGroupSeparator;
ClaimSummary.CardGroupFields = CardGroupFields;
ClaimSummary.Header = Header;
ClaimSummary.VictimMinorBadge = VictimMinorBadge;
ClaimSummary.Occurrence = Occurrence;
ClaimSummary.OccurrenceLocationFields = OccurrenceLocationFields;
